/**
 * Engine-aware FilterGroup -> WHERE clause body builder.
 *
 * Returns the body only (no leading `WHERE`). Each group is OR-ed internally;
 * groups are AND-ed together. Kept byte-equivalent to the electron-side
 * `electron/table-explorer/queryBuilder/queryUtils.ts` (`buildConditionSql` +
 * assembler): a scalar `enum` value emits `= x`, a `(string | number)[]`
 * emits `IN (...)` — numeric elements UNQUOTED (sargable, decision #4 of the
 * on-demand FK labels design) and string elements quoted/escaped, both via
 * the same per-engine `esc`/`escapeValue` — as do the "in list" / "not in
 * list" operators (`IN` / `NOT IN`; `inListSql`, which also splits a list
 * past Oracle's 1,000-value limit). An empty array emits no
 * predicate. Both assemblers drop empty (inactive) fragments. A JS boolean
 * value emits the shared `sqlBooleanLiteral` policy, which the electron
 * escapers apply too. A comparison on a date / datetime / timestamp column
 * (the caller passes `valueDataType`, or `buildFilterWhereClause`'s
 * `columnTypeOf`) emits the shared `sqlTemporalLiteral` policy — every
 * comparison operator, a plain-column `lookup` and each `enum` value included
 * — like `buildWhereClause`'s `columnTypes` in the electron builder. The
 * same column type writes a number too precise for a JS number (kept as its
 * text) bare on a numeric column (`sqlNumericLiteral`), and a `lookup` on a
 * PostgreSQL array column as array membership (`emittedFilterOperator`,
 * shared with the electron builder). A contains value matches literally
 * (`containsLikeSql`), and a SQL Server text value is written `N'…'` by the
 * same column type (`sqlServerTextLiteral.ts`).
 * Change the two together or the Table Explorer grid and the
 * Data-Profile/FilterWizard paths diverge.
 */

import { quoteMysqlText } from '../../../../core/utils/mysqlSqlLiteral';
import { sqlBooleanLiteral } from '../../../../core/utils/sqlBooleanLiteral';
import { withSqlServerNationalPrefix } from '../../../../core/utils/sqlServerTextLiteral';
import { sqlTemporalLiteral } from '../../../../core/utils/sqlTemporalLiteral';
import type { DatabaseEngineId } from '../../../../core/engines';
import type { FilterCondition, FilterGroup } from '../../core/types/workspaceTypes';
import { quoteIdent } from '../../data-profile/utils/sqlIdentifierQuoting';
import { emittedFilterOperator } from './arrayColumnLookup';
import { containsLikeSql } from './containsLikeSql';
import { inListSql } from './inListFilterSql';
import { buildJsonConditionSql, escapeAsTextLiteral, extractTextSql } from './jsonFilterSql';
import { buildSpatialConditionSql } from './spatialFilterSql';
import { sqlNumericLiteral } from './sqlNumericLiteral';

/** `dataType` — the compared column's type: SQL Server's `N'…'` (`sqlServerTextLiteral.ts`). */
function escapeValue(value: unknown, engine: DatabaseEngineId, dataType?: string): string {
  if (value === null || value === undefined) return 'NULL';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return sqlBooleanLiteral(value, engine);
  if (value instanceof Date) return `'${value.toISOString()}'`;
  if (engine === 'mysql') return quoteMysqlText(String(value));
  const quoted = `'${String(value).replace(/'/g, "''")}'`;
  return engine === 'mssql' ? withSqlServerNationalPrefix(quoted, value, dataType) : quoted;
}

/**
 * Optional per-call emit context.
 *
 * `quoteSiblingField` — how to reference a SIBLING column of `quotedField`'s
 * relation (the T19 declared lat/lng pair's longitude). Multi-table callers
 * (the Visual Query Editor) pass one that applies the SAME qualifier as
 * `quotedField` (`p."lng"` next to `p."lat"`), so a join where both tables
 * have a `lng` column filters the right one. Omitted → the bare quoted
 * column name, which is correct for single-table callers (Table Explorer,
 * Data Profile).
 *
 * `valueDataType` — the compared column's engine data type (for HAVING
 * `MIN`/`MAX`, the argument's). On a date / datetime / timestamp column the
 * comparison values (`=`, `!=`, `<`, `<=`, `>`, `>=`, `BETWEEN`, `lookup`,
 * `enum`) are written with `sqlTemporalLiteral` — Oracle has no ISO literal
 * and SQL Server needs the seconds. On a PostgreSQL array column (`integer[]`)
 * a `lookup` is written as membership, `<value> = ANY(<col>)`. On SQL Server
 * it decides the `N'…'` prefix of every text value (`sqlServerTextLiteral.ts`).
 * Omitted → the plain escaper, `lookup` compares with `=`, and a SQL Server
 * text value takes `N` only when it holds a character outside ASCII.
 */
export type ConditionSqlOptions = {
  readonly quoteSiblingField?: (columnName: string) => string;
  readonly valueDataType?: string;
};

export function buildConditionSql(
  quotedField: string,
  condition: FilterCondition,
  engine: DatabaseEngineId,
  options?: ConditionSqlOptions,
): string {
  const { value, value2 } = condition;
  const operator = emittedFilterOperator(condition.operator, engine, options?.valueDataType);
  // PostgreSQL json has no implicit text conversion for LIKE/LOWER. Text
  // operators search its serialized form; existing text columns keep theirs.
  const textField = engine === 'pgsql' ? `${quotedField}::text` : quotedField;
  const esc = (v: unknown) => escapeValue(v, engine, options?.valueDataType);
  // Comparison values: a temporal column's literal, a numeric column's number
  // too precise for a JS number (bare), else the plain escaper.
  const cmp = (v: unknown) =>
    sqlTemporalLiteral(v, engine, options?.valueDataType) ?? sqlNumericLiteral(v, options?.valueDataType) ?? esc(v);

  switch (operator) {
    case 'IS NULL':
      return `${quotedField} IS NULL`;
    case 'IS NOT NULL':
      return `${quotedField} IS NOT NULL`;
    case '=':
      return `${quotedField} = ${cmp(value)}`;
    case 'enum':
      // `enum` is a dropdown-backed filter. A scalar value behaves as `=`;
      // a `(string | number)[]` value (multiselect) emits `IN (...)`,
      // escaping each element with the same per-engine escaper — a `number`
      // element emits UNQUOTED (`esc`/`escapeValue`'s `typeof === 'number'`
      // branch), keeping a numeric FK-key `IN` list sargable instead of
      // stringifying every value (decision #4). An empty array means the
      // user selected nothing — emit no predicate (inactive).
      if (Array.isArray(value)) return inListSql(quotedField, value, cmp, engine);
      return `${quotedField} = ${cmp(value)}`;
    case 'IN':
    case 'NOT IN':
      // "in list" / "not in list": each value as `=` writes it (`cmp`); an
      // empty list is inactive. Oracle's 1,000-value limit: `inListSql`.
      return inListSql(quotedField, Array.isArray(value) ? value : [], cmp, engine, operator === 'NOT IN');
    case 'lookup':
      // Loose-FK on a JSON column declares a `sourcePath` (e.g.
      // `metadata.$.customer_id` → `customers.id`). Emit a JSON extract on
      // the source so the lookup compares the path's value, not the column.
      // Mirrors `path_eq` semantics for consistency with manual JSON filters.
      // Both sides emit as text — `extractTextSql` returns text on every
      // engine (CAST-wrapped on SQLite) and `escapeAsTextLiteral` quote-coerces
      // the value side. See `escapeAsTextLiteral`'s header for the rationale.
      if (condition.sourcePath) {
        return `${extractTextSql(engine, quotedField, condition.sourcePath)} = ${escapeAsTextLiteral(value, engine)}`;
      }
      return `${quotedField} = ${cmp(value)}`;
    case '!=':
      return `${quotedField} <> ${cmp(value)}`;
    case '<':
      return `${quotedField} < ${cmp(value)}`;
    case '<=':
      return `${quotedField} <= ${cmp(value)}`;
    case '>':
      return `${quotedField} > ${cmp(value)}`;
    case '>=':
      return `${quotedField} >= ${cmp(value)}`;
    case 'LIKE':
      return `${textField} LIKE ${esc(value)}`;
    case 'CONTAINS':
      // Case-insensitive — LOWER() works across all supported engines
      // (MSSQL/PostgreSQL/MySQL/Oracle/SQLite) and is independent of the
      // column's collation, so behavior is consistent everywhere. The value
      // matches literally (`containsLikeSql`: `%` / `_` escaped).
      return containsLikeSql(textField, value, engine, esc);
    case 'BETWEEN':
      return `${quotedField} BETWEEN ${cmp(value)} AND ${cmp(value2)}`;
    case 'array_contains':
      return engine === 'pgsql'
        ? `${esc(value)} = ANY(${quotedField})`
        : `${quotedField} = ${esc(value)}`;
    case 'array_not_contains':
      return engine === 'pgsql'
        ? `${esc(value)} <> ALL(${quotedField})`
        : `${quotedField} <> ${esc(value)}`;
    case 'array_is_empty':
      return `cardinality(${quotedField}) = 0`;
    case 'array_is_not_empty':
      return `cardinality(${quotedField}) > 0`;
    case 'array_length_eq':
      return `cardinality(${quotedField}) = ${esc(value)}`;
    case 'array_length_neq':
      return `cardinality(${quotedField}) <> ${esc(value)}`;
    case 'array_length_gt':
      return `cardinality(${quotedField}) > ${esc(value)}`;
    case 'array_length_lt':
      return `cardinality(${quotedField}) < ${esc(value)}`;
    case 'array_length_gte':
      return `cardinality(${quotedField}) >= ${esc(value)}`;
    case 'array_length_lte':
      return `cardinality(${quotedField}) <= ${esc(value)}`;
    case 'json':
      // The JSON functions return text of their own type, not the column's:
      // the values are written without the column type (SQL Server `N'…'`).
      return buildJsonConditionSql(quotedField, condition, engine, (v) => escapeValue(v, engine));
    case 'spatial': {
      // Delegate to the SHARED spatial emitter (byte-identical to the electron
      // TE builder). `quotedLngField` is the T19 declared-pair sibling, quoted
      // with the same per-engine quoting as the main field — and qualified
      // like it when the caller supplies `quoteSiblingField`.
      const lngCol = condition.spatialContext?.lngColumn;
      const quoteSibling = options?.quoteSiblingField ?? ((name: string) => quoteIdent(engine, name));
      return buildSpatialConditionSql(
        quotedField,
        condition,
        engine,
        lngCol ? { quotedLngField: quoteSibling(lngCol) } : undefined,
      );
    }
    default:
      return `${quotedField} = ${esc(value)}`;
  }
}

/**
 * `columnTypeOf` — the filtered table's column → engine data type (Data
 * Profile passes its table metadata), so a date filter takes the temporal
 * literal exactly as Table Explorer's `columnTypes` does. Omitted → no
 * temporal rewrite (the value may be compared with a text column).
 */
export function buildFilterWhereClause(
  filters: FilterGroup[] | undefined,
  engine: DatabaseEngineId,
  columnTypeOf?: (columnName: string) => string | undefined,
): string {
  if (!filters || filters.length === 0) return '';

  const groups = filters
    .filter((g) => g.conditions.length > 0)
    .map((g) => {
      const quoted = quoteIdent(engine, g.field);
      const valueDataType = columnTypeOf?.(g.field);
      const parts = g.conditions
        .map((c) => buildConditionSql(quoted, c, engine, valueDataType ? { valueDataType } : undefined))
        .filter((s) => s && s.length > 0);
      const joined = parts.join(' OR ');
      return parts.length > 1 ? `(${joined})` : joined;
    })
    .filter((s) => s.length > 0);

  return groups.join(' AND ');
}
